/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useCallback, useEffect, useState, useRef } from 'react'
import { Spin, Input } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import Text from '../../ts-components/Text'
import { Organization } from '@sportninja/common/types/Organization'
import { Team } from '@sportninja/common/types/TeamStanding'
import { Schedule } from '@sportninja/common/types/Schedule'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import req from '@sportninja/common/api/request'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import ChevronDownIcon from './ChevronDownIcon'

/**
 * @interface TargetAudienceSelectorProps
 * @property {Organization[]} orgs - Array of available organizations to select from
 * @property {Team[]} teams - Array of available teams to select from
 * @property {Schedule[]} scheds - Array of available schedules/competitions to select from
 * @property {string | undefined} selected - ID of the currently selected entity
 * @property {(value: any) => void} setSelected - Callback to update the selected entity ID
 * @property {string | null} selectedType - Type of the currently selected entity ('org', 'team', 'sched')
 * @property {(value: string | null) => void} setSelectedType - Callback to update the selected entity type
 * @property {number | null} targetAudience - Number of users in the target audience
 * @property {(value: number | null) => void} setTargetAudience - Callback to update the target audience count
 * @property {(value: string) => void} setParentId - Callback to set the parent entity ID
 * @property {(value: 'team' | 'schedule' | 'organization' | undefined) => void} setParentEntityType - Callback to set the parent entity type
 * @property {boolean} disabled - Whether the selector is disabled (e.g., during loading or edit mode)
 */
interface TargetAudienceSelectorProps {
  orgs: Organization[]
  teams: Team[]
  scheds: Schedule[]
  selected: string | undefined
  setSelected: (value: any) => void
  selectedType: string | null
  setSelectedType: (value: string | null) => void
  targetAudience: number | null
  setTargetAudience: (value: number | null) => void
  setParentId: (value: string) => void
  setParentEntityType: (
    value: 'team' | 'schedule' | 'organization' | undefined
  ) => void
  disabled: boolean
}

/**
 * Target Audience Selector component - handles selection of organizations, teams, and schedules
 *
 * This component provides a dropdown interface for users to select the target audience for their post.
 * It displays available organizations, teams, and schedules in a searchable dropdown menu.
 *
 * Key features:
 * - Displays entity thumbnails and names
 * - Groups entities by type (Organizations, Teams, Schedules)
 * - Fetches and displays target audience count for selected entity
 * - Handles click-outside-to-close functionality
 * - Supports disabled state for edit mode
 * - Automatically maps entity types for API compatibility
 *
 * The component is memoized to prevent unnecessary re-renders and includes proper
 * cleanup for event listeners to prevent memory leaks.
 *
 * @param props - Component props as defined in TargetAudienceSelectorProps
 * @returns JSX element representing a dropdown selector for target audience
 */
const TargetAudienceSelector: React.FC<TargetAudienceSelectorProps> =
  React.memo(
    ({
      orgs,
      teams,
      scheds,
      selected,
      setSelected,
      selectedType,
      setSelectedType,
      setTargetAudience,
      setParentId,
      setParentEntityType,
      disabled,
    }) => {
      const [open, setOpen] = useState(false)
      const [isLoadingAudience, setIsLoadingAudience] = useState(false)
      const [searchTerm, setSearchTerm] = useState('')
      const ref = useRef<HTMLDivElement>(null)

      useEffect(() => {
        const handler = (e: MouseEvent) => {
          if (ref.current && !ref.current.contains(e.target as Node)) {
            setOpen(false)
          }
        }
        if (open) {
          document.addEventListener('mousedown', handler)
        }
        return () => document.removeEventListener('mousedown', handler)
      }, [open])

      const handleSelect = useCallback(
        (entity: any, type: string) => {
          setSelected(entity.id)
          setSelectedType(type)
          setParentId(entity.id)
          setParentEntityType(
            type === 'org'
              ? 'organization'
              : type === 'sched'
              ? 'schedule'
              : 'team'
          )
          setSearchTerm('')
          setOpen(false)
        },
        [setSelected, setSelectedType, setParentId, setParentEntityType]
      )

      useEffect(() => {
        // @ts-ignore
        const getTargetAudience = async () => {
          if (!selected || !selectedType) {
            setTargetAudience(null)
            setIsLoadingAudience(false)
            return
          }
          try {
            setIsLoadingAudience(true)
            let type = ''
            if (selectedType === 'org') {
              type = 'organization'
            } else if (selectedType === 'team') {
              type = 'team'
            } else if (selectedType === 'sched') {
              type = 'competition'
            }
            const { data } = await req(`/social-feed/${selected}/${type}`)
            if (data?.total_users > 0) {
              setTargetAudience(data.total_users)
            } else {
              setTargetAudience(null)
            }
          } catch {
            setTargetAudience(null)
          } finally {
            setIsLoadingAudience(false)
          }
        }
        // getTargetAudience()
      }, [selected, selectedType, setTargetAudience])

      const getSelectedEntity = useCallback(() => {
        const all = [...orgs, ...teams, ...scheds]
        return all.find((e) => e.id === selected)
      }, [orgs, teams, scheds, selected])

      // Filter entities based on search term
      const filterEntities = useCallback(
        (entities: any[], searchTerm: string) => {
          if (!searchTerm.trim()) {
            return entities
          }
          return entities.filter(
            (entity) =>
              entity?.name_full
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
              entity?.name?.toLowerCase().includes(searchTerm.toLowerCase())
          )
        },
        []
      )

      const filteredOrgs = filterEntities(orgs, searchTerm)
      const filteredTeams = filterEntities(teams, searchTerm)
      const filteredScheds = filterEntities(scheds, searchTerm)

      // Helper function to format organization subtitle
      const getOrgSubtitle = useCallback((org: Organization) => {
        const parts: string[] = []
        if (org.teams_count) {
          const term = org.teams_count === 1 ? 'Team' : 'Teams'
          parts.push(`${org.teams_count} ${term}`)
        }
        if (org.schedules_count) {
          const term =
            org.schedules_count === 1
              ? isCanlan
                ? 'Season'
                : 'Competition'
              : isCanlan
              ? 'Seasons'
              : 'Competitions'
          parts.push(`${org.schedules_count} ${term}`)
        }
        if (org.children_count) {
          const term =
            org.children_count === 1
              ? isCanlan
                ? 'Sub League'
                : 'Sub Org'
              : isCanlan
              ? 'Sub Leagues'
              : 'Sub Orgs'
          parts.push(`${org.children_count} ${term}`)
        }
        return parts.join(' • ')
      }, [])

      // Helper function to format schedule subtitle
      const getSchedSubtitle = useCallback((sched: Schedule) => {
        const parts: string[] = []
        if (sched.games_count) {
          const term = sched.games_count === 1 ? 'Game' : 'Games'
          parts.push(`${sched.games_count} ${term}`)
        }

        // Format dates
        if (sched.starts_at && sched.ends_at) {
          const startDate = new Date(sched.starts_at)
          const endDate = new Date(sched.ends_at)
          const now = new Date()

          const formatDate = (date: Date) => {
            return date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year:
                date.getFullYear() !== now.getFullYear()
                  ? 'numeric'
                  : undefined,
            })
          }

          const dateRange = `${formatDate(startDate)} - ${formatDate(endDate)}`
          const isEnded = endDate < now

          parts.push(isEnded ? `${dateRange} (Ended)` : dateRange)
        }

        return parts.join(' • ')
      }, [])

      return (
        <div ref={ref}>
          <div
            css={css`
              padding: 8px 8px;
              gap: 4px;
              background-color: ${colors.SECONDARY_400};
              border-radius: 8px;
              color: ${colors.NEUTRAL_0};
              display: inline-flex;
              align-items: center;
              cursor: ${disabled ? 'not-allowed' : 'pointer'};
            `}
            onClick={() => !disabled && setOpen((v) => !v)}
          >
            {getSelectedEntity() ? (
              <>
                {isLoadingAudience ? (
                  <div
                    css={css`
                      width: 16px;
                      height: 16px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                    `}
                  >
                    <Spin size='small' />
                  </div>
                ) : (
                  <img
                    src={
                      getImageThumbnailId(getSelectedEntity()) ||
                      (selectedType === 'org'
                        ? '/images/generic_placeholders/placeholder_organization.png'
                        : selectedType === 'team'
                        ? '/images/generic_placeholders/placeholder_team.png'
                        : '/images/generic_placeholders/placeholder_competition.png')
                    }
                    alt={
                      getSelectedEntity()?.name_full ||
                      getSelectedEntity()?.name
                    }
                    width={16}
                    height={16}
                    css={css`
                      border-radius: 4px;
                      border: 2px solid ${colors.WHITE};
                      background: white;
                      object-fit: contain;
                    `}
                  />
                )}

                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.NEUTRAL_0}
                  style={`                
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;`}
                >
                  {getSelectedEntity()?.name_full}
                </Text>
                <ChevronDownIcon fill={colors.NEUTRAL_0} />
              </>
            ) : (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 4px;
                `}
              >
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={colors.PRIMARY}
                  style={`                
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;`}
                >
                  Select Audience
                </Text>
                <ChevronDownIcon fill={colors.NEUTRAL_0} />
              </div>
            )}
          </div>
          {open && !disabled && (
            <div
              css={css`
                position: absolute;
                z-index: 1000;
                background: ${colors.SECONDARY_300};
                border: 1px solid ${colors.SECONDARY_100};
                border-radius: 8px;
                margin-top: 4px;
                min-width: 220px;
                max-height: 400px;
                overflow-y: auto;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                padding: 8px 0;
              `}
            >
              <div
                css={css`
                  padding: 0px 8px 8px 8px;
                  border-bottom: 1px solid ${colors.SECONDARY_200};
                  margin-bottom: 8px;
                `}
              >
                <Input
                  placeholder='Search...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size='small'
                  autoFocus
                  css={css`
                    background: ${colors.SECONDARY_400};
                    border: 1px solid ${colors.SECONDARY_100};
                    color: ${colors.NEUTRAL_0};

                    &:focus,
                    &:hover {
                      border-color: ${colors.PRIMARY};
                      background: ${colors.SECONDARY_400};
                    }

                    .ant-input {
                      background: transparent;
                      color: ${colors.NEUTRAL_0};
                      border: none;

                      &::placeholder {
                        color: ${colors.NEUTRAL_100};
                      }
                    }
                  `}
                />
              </div>
              {filteredOrgs.length > 0 && (
                <>
                  <div
                    css={css`
                      padding: 8px 12px;
                      border-bottom: 1px solid ${colors.SECONDARY_200};
                      margin-bottom: 4px;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        justify-content: flex-start;
                      `}
                    >
                      <Text
                        variant='paragraphSmall'
                        weight='semiBold'
                        color={colors.PRIMARY}
                        style='text-transform: uppercase;'
                      >
                        {isCanlan ? 'Leagues' : 'Organizations'}
                      </Text>
                    </div>
                  </div>
                  {filteredOrgs
                    .sort(
                      (a, b) =>
                        a?.name_full?.localeCompare(b?.name_full) ||
                        a?.name?.localeCompare(b?.name)
                    )
                    .map((org) => (
                      <div
                        key={org.id}
                        css={css`
                          padding: 8px 12px;
                          display: flex;
                          align-items: center;
                          gap: 12px;
                          cursor: pointer;
                          border-radius: 6px;
                          margin: 2px 4px;
                          transition: background-color 0.2s ease;
                          &:hover {
                            background: ${colors.SECONDARY_200};
                          }
                        `}
                        onClick={() => handleSelect(org, 'org')}
                      >
                        <img
                          src={
                            getImageThumbnailId(org) ||
                            '/images/generic_placeholders/placeholder_organization.png'
                          }
                          alt={org?.name_full || org?.name}
                          width={24}
                          height={24}
                          css={css`
                            border-radius: 4px;
                            border: 1px solid ${colors.WHITE};
                            object-fit: contain;
                            background: white;
                          `}
                        />
                        <div
                          css={css`
                            flex: 1;
                            min-width: 0;
                          `}
                        >
                          <div
                            css={css`
                              color: ${colors.NEUTRAL_0};
                              font-size: 14px;
                              font-weight: 500;
                              white-space: nowrap;
                              overflow: hidden;
                              text-overflow: ellipsis;
                              line-height: 1.4;
                            `}
                          >
                            {org?.name_full || org?.name}
                          </div>
                          {getOrgSubtitle(org) && (
                            <div
                              css={css`
                                color: ${colors.NEUTRAL_100};
                                font-size: 12px;
                                font-weight: 400;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                margin-top: 2px;
                                line-height: 1.3;
                              `}
                            >
                              {getOrgSubtitle(org)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </>
              )}
              {filteredScheds.length > 0 && (
                <>
                  <div
                    css={css`
                      padding: 8px 12px;
                      border-bottom: 1px solid ${colors.SECONDARY_200};
                      margin-bottom: 4px;
                      ${filteredOrgs.length > 0 ? 'margin-top: 8px;' : ''}
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        justify-content: flex-start;
                      `}
                    >
                      <Text
                        variant='paragraphSmall'
                        weight='semiBold'
                        color={colors.PRIMARY}
                        style='text-transform: uppercase;'
                      >
                        {isCanlan ? 'Seasons' : 'Competitions'}
                      </Text>
                    </div>
                  </div>
                  {filteredScheds
                    .sort((a, b) => {
                      const dateA = new Date(a.starts_at || 0).getTime()
                      const dateB = new Date(b.starts_at || 0).getTime()
                      return dateB - dateA // Most recent first
                    })
                    .map((sched) => (
                      <div
                        key={sched.id}
                        css={css`
                          padding: 8px 12px;
                          display: flex;
                          align-items: center;
                          gap: 12px;
                          cursor: pointer;
                          border-radius: 6px;
                          margin: 2px 4px;
                          transition: background-color 0.2s ease;
                          &:hover {
                            background: ${colors.SECONDARY_200};
                          }
                        `}
                        onClick={() => handleSelect(sched, 'sched')}
                      >
                        <img
                          src={
                            getImageThumbnailId(sched) ||
                            '/images/generic_placeholders/placeholder_competition.png'
                          }
                          alt={sched?.name_full || sched?.name}
                          width={24}
                          height={24}
                          css={css`
                            border-radius: 4px;
                            border: 1px solid ${colors.WHITE};
                            object-fit: contain;
                            background: white;
                          `}
                        />
                        <div
                          css={css`
                            flex: 1;
                            min-width: 0;
                          `}
                        >
                          <div
                            css={css`
                              color: ${colors.NEUTRAL_0};
                              font-size: 14px;
                              font-weight: 500;
                              white-space: nowrap;
                              overflow: hidden;
                              text-overflow: ellipsis;
                              line-height: 1.4;
                            `}
                          >
                            {sched?.name_full || sched?.name}
                          </div>
                          {getSchedSubtitle(sched) && (
                            <div
                              css={css`
                                color: ${colors.NEUTRAL_100};
                                font-size: 12px;
                                font-weight: 400;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                margin-top: 2px;
                                line-height: 1.3;
                              `}
                            >
                              {getSchedSubtitle(sched)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </>
              )}
              {filteredTeams.length > 0 && (
                <>
                  <div
                    css={css`
                      padding: 8px 12px;
                      border-bottom: 1px solid ${colors.SECONDARY_200};
                      margin-bottom: 4px;
                      ${filteredOrgs.length > 0 || filteredScheds.length > 0
                        ? 'margin-top: 8px;'
                        : ''}
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        justify-content: flex-start;
                      `}
                    >
                      <Text
                        variant='paragraphSmall'
                        weight='semiBold'
                        color={colors.PRIMARY}
                        style='text-transform: uppercase;'
                      >
                        Teams
                      </Text>
                    </div>
                  </div>
                  {filteredTeams
                    .sort(
                      (a, b) =>
                        a?.name_full?.localeCompare(b?.name_full) ||
                        a?.name?.localeCompare(b?.name)
                    )
                    .map((team) => (
                      <div
                        key={team.id}
                        css={css`
                          padding: 8px 12px;
                          display: flex;
                          align-items: center;
                          gap: 12px;
                          cursor: pointer;
                          border-radius: 6px;
                          margin: 2px 4px;
                          transition: background-color 0.2s ease;
                          &:hover {
                            background: ${colors.SECONDARY_200};
                          }
                        `}
                        onClick={() => handleSelect(team, 'team')}
                      >
                        <img
                          src={
                            getImageThumbnailId(team) ||
                            '/images/generic_placeholders/placeholder_team.png'
                          }
                          alt={team?.name_full || team?.name}
                          width={24}
                          height={24}
                          css={css`
                            border-radius: 4px;
                            border: 1px solid ${colors.WHITE};
                            object-fit: contain;
                            background: white;
                          `}
                        />
                        <div
                          css={css`
                            flex: 1;
                            min-width: 0;
                          `}
                        >
                          <div
                            css={css`
                              color: ${colors.NEUTRAL_0};
                              font-size: 14px;
                              font-weight: 500;
                              white-space: nowrap;
                              overflow: hidden;
                              text-overflow: ellipsis;
                              line-height: 1.4;
                            `}
                          >
                            {team?.name_full || team?.name}
                          </div>
                        </div>
                      </div>
                    ))}
                </>
              )}
            </div>
          )}
        </div>
      )
    }
  )

export default TargetAudienceSelector
